import React from 'react';
import {
    Button, Col, Collapse, FormGroup,
    Row,
    UncontrolledTooltip,
} from 'reactstrap';
import { isEqual, debounce } from 'lodash-es';
import { v7 as uuid } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLock, faSave, faTasks, faUnlock, faUser,
    faUserFriends,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { LANGUAGE_LABELS } from '@s20/i18n';
import { upsert } from '@s20/common/util/array';
import { clone } from '@s20/common/util/object';
import { patterns, phone as phoneUtil } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import { TipAlert } from '../common/TipAlert';
import {
    FlexStartRow,
    onFieldChange,
    toasty,
    AppPageForm,
    FlexCenterRow,
    FormCheckbox,
    FormGroupColumn,
    FormLabel,
    GroupedRow,
    SubHeading,
    onReactSelectChanged,
    Input,
    FormCircularProgress,
    FlexColumnCenter,
} from '../common/forms/FormElements';
import { Address } from '../address/Address';
import { Can } from '../Can';
import CommonContext, {
    ApiRoutes,
    ApplicationPermissions,
    AppNavPaths,
} from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import AddressFormNew from '../address/AddressFormNew';
import { handleFormSaveError } from '../common/forms/ValidationError';
import authService from '../api-authorization/AuthorizeService';
import { Block } from '../common/Block';
import { Modal } from '../common/Modal';
import Chip from '../common/forms/Chip';
import { EmployeePayRate, EmployeePayRateForm } from './EmployeePayRateForm';
import { Employee, EmployeeLocationTypes } from './Employee';
import { EmployeeAddressInfo } from './EmployeeAddressInfo';
import { EmployeePayRateInfo } from './EmployeePayRateInfo';
import {
    EmployeeRestriction,
    EmployeeRestrictionForm,
} from './EmployeeRestrictionForm';
import { EmployeeRestrictionInfo } from './EmployeeRestrictionInfo';
import { EmployeeLongTermStatusInfo } from './EmployeeLongTermStatusInfo';
import { EmployeeCertificationInfo } from './EmployeeCertificationInfo';
import { EmployeeLongTermStatusForm } from './EmployeeLongTermStatusForm';
import { EmployeeCertificationForm } from './EmployeeCertificationForm';
import EmployeeHistoryForm from './EmployeeHistoryForm';
import { EmployeeMetrics } from './EmployeeMetrics';
import { EmployeeCountyIncentiveInfo } from './EmployeeCountyIncentiveInfo';
import { EmployeeCountyIncentiveForm } from './EmployeeCountyIncentiveForm';
import { EmployeeRecentTimesheetsInfo } from './EmployeeRecentTimesheetsInfo';
import './EmployeeForm.scss';

const notifyError = (message) => toasty.error('Save Unsuccessful', message);
const notifySuccess = () => toasty.success('Employee Saved', 'Employee saved successfully.');

// #region CONSTANTS
const AccountStatus = {
    Active: 1,
    Inactive: 2,
    NewHire: 3,
};

// #endregion

class EmployeeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();
        this.addressFormRef = React.createRef();
        this.employeeLongTermStatusFormRef = React.createRef();
        this.employeePayRateFormRef = React.createRef();
        this.employeeCertificationFormRef = React.createRef();
        this.restrictionFormRef = React.createRef();
        this.employeeCountyIncentiveFormRef = React.createRef();

        this.state = {
            addressFormTitle: '',
            addressFormValidated: false,
            companies: [],
            employee: new Employee(),
            countyGroups: [],
            formOpened: false,
            formValidated: false,
            groups: [],
            loading: true,
            dispatchCompanies: [],
            expandedDispatchCompanies: [],
            onboarding: false,
            payRateFormValidated: false,
            restrictionFormValidated: false,
            saving: false,
            selectedAddress: null,
            selectedPayRate: null,
            selectedRestriction: null,
            statuses: [],
            showAddressForm: false,
            showPayRateForm: false,
            showRestrictionForm: false,
            showEmployeeLongTermStatusForm: false,
            showEmployeeCertificationForm: false,
            showEmployeeHistory: false,
            employeeHistoryType: null,
            isAdmin: false,
            longTermStatusManagementEnabled: false,
            enableCertification: false,
            employeeLongTermStatuses: [],
            employeCertifications: [],
            selectedEmployeeLongTermStatus: null,
            selectedEmployeeCertification: null,
            pageModified: false,
            impersonateLoading: false,
            cancelImpersonateLoading: false,
            contractScheduleTypes: [],
            showActiveAssignmentsModal: false,
            showActiveJobNumbers: [],
            unlockLoading: false,
            originalAccountStatusId: null,
            tenantLanguageIds: [],
            showCountyIncentiveForm: false,
            countyIncentiveFormValidated: false,
            selectedIncentive: null,
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onAddAddress = this.onAddAddress.bind(this);
        this.onEditAddress = this.onEditAddress.bind(this);
        this.onSaveAddress = this.onSaveAddress.bind(this);
        this.saveEmployee = this.saveEmployee.bind(this);
        this.saveNewEmployee = this.saveNewEmployee.bind(this);
        this.onEditPayRate = this.onEditPayRate.bind(this);
        this.onSavePayRate = this.onSavePayRate.bind(this);
        this.onEditRestriction = this.onEditRestriction.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onChange = this.onChange.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.onSaveCountyIncentive = this.onSaveCountyIncentive.bind(this);
        this.onIncentiveChange = this.onIncentiveChange.bind(this);
        this.onCountyChange = this.onCountyChange.bind(this);
        this.onRemoveIncentive = this.onRemoveIncentive.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    onAccountStatusChanged = (selection) => {
        this.setState((state) => {
            const { employee, statuses } = state;

            if (selection) {
                employee.accountStatus = statuses.find(
                    (s) => s.id === selection.id,
                );

                if (employee.accountStatus.id == AccountStatus.Inactive) {
                    employee.systemAdded = false;
                }
            } else {
                employee.accountStatus = null;
            }

            return { employee };
        });
    };

    onActiveClicked = () => {
        this.setState((state) => {
            const emp = state.employee;
            emp.userDetails.active = true;
            return { employee: emp };
        });
    };

    onAddAddress = (type) => {
        const { employee } = this.state;

        const new_address = new Address(employee.countryId, employee.countryCode);
        // This is a new address, embed the emp Id and company.
        new_address.employeeId = employee.id;
        new_address.name = `Home - ${employee.firstName} ${employee.lastName}`;
        new_address.employeeLocationTypeId = type === 'home'
            ? EmployeeLocationTypes.Home
            : EmployeeLocationTypes.Routing;

        new_address.employeeLocationType = {
            id:
                type === 'home'
                    ? EmployeeLocationTypes.Home
                    : EmployeeLocationTypes.Routing,
            name: type === 'home' ? 'Employee' : 'Employee Routing',
        }; // TODO: remove inline ints
        const formTitle = `New ${type === 'home' ? 'Home' : 'Routing'} Address`;
        this.addressFormRef.current.open(new_address, formTitle);
    };

    onAddEmployeeLongTermStatus = () => {
        const { employee } = this.state;

        this.employeeLongTermStatusFormRef.current.open(employee.id);
        this.setState({
            showEmployeeLongTermStatusForm: true,
            employeeId: employee.id,
            selectedEmployeeLongTermStatus: null,
        });
    };

    onAddEmployeeCertification = () => {
        const { employee } = this.state;
        this.employeeCertificationFormRef.current.open(employee.id);

        this.setState({
            showEmployeeCertificationForm: true,
            employeeId: employee.id,
            selectedEmployeeCertification: null,
        });
    };

    onAddPayRate = () => {
        const { employee } = this.state;

        const new_pay_rate = new EmployeePayRate();
        new_pay_rate.employeeId = employee.id;

        this.setState({
            selectedPayRate: new_pay_rate,
            showPayRateForm: true,
            payRateFormValidated: false,
            pageModified: true,
        });

        this.context.setFormOpened(true);
    };

    onAddRestriction = () => {
        const { employee } = this.state;
        const new_restriction = new EmployeeRestriction();
        new_restriction.employeeId = employee.id;

        this.restrictionFormRef.current.open(new_restriction, 'New Restriction');

        this.setState({
            showRestrictionForm: true,
            pageModified: true,
        });
    };

    // #region METHODS
    onChange = onFieldChange;

    onClickImpersonation = async () => {
        const { employee } = this.state;

        this.setState({ impersonateLoading: true });

        try {
            const response = await httpClient.put(
                ApiRoutes.employee.toggleImpersonation(),
                { EmployeeId: employee.id },
                httpClient.format.json,
            );

            if (!response.data) {
                window.sessionStorage.removeItem('impersonating');
            } else {
                window.sessionStorage.setItem('impersonating', response.data);
            }

            window.location.href = '/';
        } catch (error) {
            notifyError(error.toString());
        } finally {
            this.setState({ impersonateLoading: false });
        }
    };

    onUnlockEmployee = async () => {
        const { employee } = this.state;

        this.setState({ unlockLoading: true });
        try {
            const response = await httpClient.put(
                ApiRoutes.employee.unlock(employee.id),
            );

            if (response.result === 'SUCCESS') {
                toasty.success('Account Unlocked', 'The employee account has been unlocked successfully.');
                this.setState((state) => ({
                    employee: {
                        ...state.employee,
                        locked: false,
                    },
                }));
            } else {
                notifyError(response.message || 'Failed to unlock account.');
            }
        } catch (error) {
            notifyError(error.toString());
        } finally {
            this.setState({ unlockLoading: false });
        }
    };

    onCompanyChanged = async (item) => {
        if (!item) {
            this.setState((state) => ({
                employee: {
                    ...state.employee,
                    companyId: null,
                },
                dispatchCompanies: [],
            }));
            return;
        }

        const dComps = await httpClient.js(
            ApiRoutes.typeAheads.dispatchCompaniesForEmployee(null, item.id),
        );
        const mapped = (dComps ?? []).map((x) => ({
            id: x.value,
            companyName: x.label,
            parentCompanyId: x.parentCompanyId ?? null,
        }));

        this.setState((state) => {
            const emp = state.employee;
            emp.companyId = item.id;

            return { employee: emp, dispatchCompanies: mapped, pageModified: true };
        });
    };

    onDispatchCompanyChanged = (value) => {
        this.setState((state) => {
            const emp = { ...state.employee };

            if (state.onboarding && state.isAdmin) {
                emp.companyId = value?.parentCompanyId ?? emp.companyId ?? null;
            }

            emp.dispatchCompanyId = value?.id || null;

            return { employee: emp };
        });
    };

    // Address table edit click handler
    onEditAddress(address) {
        const { employee } = this.state;
        const formTitle = `Edit ${address.employeeLocationType.id === EmployeeLocationTypes.Home
            ? 'Home'
            : 'Routing'
            } Address`;
        this.addressFormRef.current.open(
            {
                countryId: employee.countryId,
                countryCode: employee.countryCode,
                ...address,
            },
            formTitle,
        );
    }

    onEditEmployeeLongTermStatus = (suppliedLongTermStatus) => {
        if (suppliedLongTermStatus.id) {
            this.employeeLongTermStatusFormRef.current.open(
                suppliedLongTermStatus,
            );
            this.setState({
                showEmployeeLongTermStatusForm: true,
            });
        }
    };

    onEditEmployeeCertification = (empCert) => {
        if (empCert?.id) {
            this.employeeCertificationFormRef.current.open(empCert);
            this.setState({
                showEmployeeCertificationForm: true,
                selectedEmployeeCertification: empCert,
            });
        }
    };

    onEditPayRate(payRate) {
        this.context.setFormOpened(true);
        this.setState({
            selectedPayRate: clone(payRate),
            showPayRateForm: true,
            payRateFormValidated: false,
            pageModified: true,
        });
    }

    onEditRestriction = (restriction) => {
        this.restrictionFormRef.current.open(restriction, 'Edit Restriction');

        this.setState({
            showRestrictionForm: true,
            pageModified: true,
        });
    };

    onEmailChanged = (event) => {
        const { value } = event.target;
        const { employee } = { ...this.state };
        employee.email = value;
        if (value) employee.emailNotify = true;
        this.setState({ employee: { ...employee }, pageModified: true });
    };
    // #endregion

    onEmailNotifyChanged = (e) => {
        const emp = this.state.employee;
        emp.emailNotify = e.target.checked;

        this.setState({ employee: emp, pageModified: true });
    };

    onExcludeDispatchChanged = (e) => {
        const emp = this.state.employee;
        emp.excludeDispatch = e.target.checked;

        // Excluding from the dispatch board makes the on-call options moot,
        // so clear both availability and the restriction banner flag.
        if (e.target.checked) {
            emp.isFlaggerAvailableForOnCall = false;
            emp.isOnCallRestricted = false;
        }

        this.setState({ employee: emp, pageModified: true });
    };

    onAvailableForOnCallChanged = (e) => {
        const emp = this.state.employee;
        emp.isFlaggerAvailableForOnCall = e.target.checked;

        this.setState({ employee: emp, pageModified: true });
    };

    onDisableOnCallBannerChanged = (e) => {
        const emp = this.state.employee;
        emp.isOnCallRestricted = e.target.checked;

        // Restricting on-call access deselects (and locks) availability.
        if (e.target.checked) emp.isFlaggerAvailableForOnCall = false;

        this.setState({ employee: emp, pageModified: true });
    };

    onExpandedDispatchCompaniesChanged = (values) => {
        const { employee } = { ...this.state };

        if ((values ?? []).length > 0) employee.expandedDispatchCompanies = values.map((x) => x.id);
        else employee.expandedDispatchCompanies = [];

        this.setState({ employee });
    };

    onFirstNameChanged = async (event) => {
        const { value } = event.target;
        const { employee } = { ...this.state };

        employee.firstName = value;

        this.setState({ employee: { ...employee } });

        if (value && employee.lastName) {
            await this.onGetUsername();
        }
    };

    onGetUsername = debounce(async () => {
        const { tenant } = { ...this.context };
        const { quirksEnabled } = { ...tenant.tenantSettings };

        if (!quirksEnabled) {
            return null;
        }

        const { employee } = { ...this.state };
        const username = `${employee.firstName.charAt(0)}${employee.lastName}`
            .replace(/[^a-zA-Z0-9]/g, '')
            .toLowerCase();
        try {
            const suggestion = await httpClient.js(
                ApiRoutes.employee.suggestUsername(username),
            );
            employee.userName = suggestion;
            this.setState({ employee: { ...employee } });
        } catch {
            toasty.error(
                'There was a server error when retrieving a username suggestion.  Please try your request again or contact support.',
            );
            return null;
        }
    }, 500);

    onGroupChange = (value) => {
        const { employee } = { ...this.state };

        employee.groups = value;

        // If we don't have a group selected with expanded dispatching,
        // make sure we clear out any previous selections.
        if (!this.showExpandDispatching()) employee.expandedDispatchCompanies = [];

        // The on-call settings only apply to field personnel. When no field
        // personnel group remains, clear the selections so they don't persist
        // (hidden) and reappear if a field group is added back.
        if (!this.isFieldPersonnel()) {
            employee.isFlaggerAvailableForOnCall = false;
            employee.isOnCallRestricted = false;
        }

        this.setState({ employee, pageModified: true });
    };

    onInactiveClicked = () => {
        this.setState((state) => {
            const emp = state.employee;
            emp.userDetails.active = false;
            return { employee: emp };
        });
    };

    onIncludeImportChanged = (e) => {
        const emp = this.state.employee;
        emp.systemAdded = e.target.checked;

        this.setState({ employee: emp, pageModified: true });
    };

    onLastNameChanged = async (event) => {
        const { value } = event.target;
        const { employee } = { ...this.state };

        employee.lastName = value;

        this.setState({ employee: { ...employee } });

        // anytime the name changes, the user needs to get a generated username again.
        if (value && employee.firstName) {
            await this.onGetUsername();
        }
    };

    onMobilePhoneChanged = (event) => {
        const { value } = event.target;
        const { employee } = { ...this.state };
        employee.mobilePhone = value;
        if (value && phoneUtil(value)) employee.smsNotify = true;
        this.setState({ employee: { ...employee }, pageModified: true });
    };

    onPayRateChange = (e) => {
        const { selectedPayRate } = this.state;
        selectedPayRate[e.target.name] = e.target.value;

        this.setState({ selectedPayRate, pageModified: true });
    };

    onSMSNotifyChanged = (e) => {
        const emp = this.state.employee;
        emp.smsNotify = e.target.checked;

        this.setState({ employee: emp, pageModified: true });
    };

    // After address save callback
    onSaveAddress = (address) => {
        const { employee } = this.state;
        employee.addresses = upsert(
            address,
            employee.addresses,
            'id',
        );

        this.setState({ employee, selectedAddress: null, pageModified: true });
    };

    onSaveLongTermStatus = async () => {
        const { employee } = this.state;

        const employeeLongTermStatuses = await httpClient.js(
            ApiRoutes.employeeLongTermStatus.allForEmployee(employee.id),
        );

        this.setState({
            employeeLongTermStatuses,
            showEmployeeLongTermStatusForm: false,
            selectedEmployeeLongTermStatus: null,
        });

        this.context.setFormOpened(false);
    };

    onSaveCertification = async () => {
        const { employee } = this.state;

        const employeeCertifications = await httpClient.js(
            ApiRoutes.employeeCertification.allForEmployee(employee.id),
        );

        this.setState({
            employeeCertifications,
            showEmployeeCertificationForm: false,
            selectedEmployeeCertification: null,
        });

        this.context.setFormOpened(false);
    };

    onSavePayRate() {
        const { selectedPayRate, employee } = this.state;

        const existing = employee.payRates.find(
            (x) => x.id === selectedPayRate.id,
        );

        employee.payRates = this.checkPayRates();

        if (existing) {
            employee.payRates[employee.payRates.indexOf(existing)] = selectedPayRate;
        } else {
            employee.payRates.push(selectedPayRate);
        }
        this.setState({
            employee,
            showPayRateForm: false,
            selectedPayRate: null,
            payRateFormValidated: false,
            pageModified: true,
        });
        this.context.setFormOpened(false);
    }

    checkPayRates() {
        const { selectedPayRate, employee } = this.state;

        const specificDate = selectedPayRate.startDate;
        const previousDay = new Date(specificDate);
        previousDay.setDate(previousDay.getDate() - 1);

        const [formattedDate] = previousDay.toISOString().split('T');

        return employee.payRates.map((rateInfo) => {
            const { startDate, endDate } = rateInfo;

            if (!endDate) {
                let updatedEndDate = formattedDate;

                if (startDate > selectedPayRate.startDate) {
                    if (!selectedPayRate.endDate) {
                        updatedEndDate = startDate;
                    } else {
                        updatedEndDate = '';
                    }
                }

                return {
                    ...rateInfo,
                    endDate: updatedEndDate,
                };
            }

            return rateInfo;
        });
    }

    onAddIncentive = () => {
        const { employee } = this.state;

        const newIncentive = {
            employeeId: employee.id,
        };

        this.setState({
            selectedIncentive: newIncentive,
            showCountyIncentiveForm: true,
            countyIncentiveFormValidated: false,
            pageModified: true,
        });

        this.context.setFormOpened(true);
    };

    onEditIncentive = (incentive) => {
        this.setState({
            selectedIncentive: clone(incentive),
            showCountyIncentiveForm: true,
            countyIncentiveFormValidated: false,
            pageModified: true,
        });
        this.context.setFormOpened(true);
    };

    onIncentiveChange = (e) => {
        const { selectedIncentive } = this.state;
        selectedIncentive[e.target.name] = e.target.value;

        this.setState({ selectedIncentive, pageModified: true });
    };

    onCountyChange = (e) => {
        const { selectedIncentive } = this.state;
        selectedIncentive.countyId = e?.id ?? null;
        selectedIncentive.countyName = e?.name ?? null;
        selectedIncentive.county = e;

        this.setState({ selectedIncentive, pageModified: true });
    };

    onRemoveIncentive = () => {
        const { selectedIncentive, employee } = this.state;

        employee.employeeUSCountyIncentives = employee.employeeUSCountyIncentives?.filter((x) => x.id !== selectedIncentive.id);

        this.setState({
            employee,
            showCountyIncentiveForm: false,
            selectedIncentive: null,
            countyIncentiveFormValidated: false,
            pageModified: true,
        });
        this.context.setFormOpened(false);
    };

    onSaveCountyIncentive() {
        const { selectedIncentive, employee } = this.state;

        const newStart = new Date(selectedIncentive.startDate).setHours(0, 0, 0, 0);
        const newEnd = selectedIncentive.endDate
            ? new Date(selectedIncentive.endDate).setHours(23, 59, 59, 999)
            : null;

        const dayBefore = (date) => {
            const d = new Date(date);
            d.setDate(d.getDate() - 1);
            d.setHours(0, 0, 0, 0);
            return d.toISOString();
        };

        const dayAfter = (date) => {
            const d = new Date(date);
            d.setDate(d.getDate() + 1);
            d.setHours(0, 0, 0, 0);
            return d.toISOString();
        };

        const incentives = employee.employeeUSCountyIncentives?.map((x) => {
            if (x.id == selectedIncentive.id || x.countyId !== selectedIncentive.countyId) {
                return x;
            }

            const xStart = new Date(x.startDate).setHours(0, 0, 0, 0);
            const xEnd = x.endDate ? new Date(x.endDate).setHours(23, 59, 59, 999) : null;

            const endDate = xStart < newStart && (!xEnd || xEnd >= newStart) ? dayBefore(newStart) : x.endDate;
            const startDate = xStart < newEnd && xStart >= newStart ? dayAfter(newEnd) : x.startDate;
            return { ...x, startDate, endDate };
        }) ?? [];

        const index = incentives.findIndex((x) => x.id == selectedIncentive.id);

        if (index > -1) {
            incentives[index] = selectedIncentive;
        } else {
            incentives.push({
                id: uuid(),
                ...selectedIncentive,
            });
        }

        employee.employeeUSCountyIncentives = incentives;

        this.setState({
            employee,
            showCountyIncentiveForm: false,
            selectedIncentive: null,
            countyIncentiveFormValidated: false,
            pageModified: true,
        });
        this.context.setFormOpened(false);
    }


    refreshEmployeeRestrictions = async () => {
        const { employee } = this.state;
        // Only fetch and update restrictions
        const restrictions = await httpClient.js(
            ApiRoutes.employeeRestriction.allForEmployee(employee.id),
        );

        this.setState({
            employee: {
                ...employee,
                restrictions,
            },
            showRestrictionForm: false,
            selectedRestriction: null,
            restrictionFormValidated: false,
        });

        this.context.setFormOpened(false);
    };


    onSubmit() {
        const { employee, dispatchCompanies } = { ...this.state };
        const { tenant } = { ...this.context };
        const { quirksEnabled, employeeOnboardingEnabled } = { ...tenant.tenantSettings };

        const selectedDispatchCompany = (dispatchCompanies ?? []).find(
            (company) => company.id === employee.dispatchCompanyId,
        );

        if (!employee.companyId && selectedDispatchCompany?.parentCompanyId) {
            employee.companyId = selectedDispatchCompany.parentCompanyId;
        }

        if (
            employeeOnboardingEnabled
            && employee.accountStatus?.id === AccountStatus.NewHire
            && !employee.id
            && quirksEnabled
        ) {
            // Validation
            if (!employee.userName) {
                toasty.error(
                    'Please generate the username before saving the employee.',
                );
                return false;
            }
        }

        // Normalize on-call selections so the saved payload is always consistent
        // with what the UI allows. The on-call inputs are hidden unless the employee
        // is field personnel and not excluded from dispatch, and a restricted employee
        // can never be available.
        if (employee.excludeDispatch || !this.isFieldPersonnel()) {
            employee.isFlaggerAvailableForOnCall = false;
            employee.isOnCallRestricted = false;
        } else if (employee.isOnCallRestricted) {
            employee.isFlaggerAvailableForOnCall = false;
        }

        /** Revert temporary hashes used in row keys. */
        for (const addr of employee.addresses) {
            addr.id = typeof addr.id === 'string' ? null : addr.id;
        }

        employee.usCountyGroups = (employee.usCountyGroups ?? []).map((group) => ({
            ...group,
            contractUnionWorkTypeList: null,
        }));

        // Clear any fluent api errors
        this.clearSaving();

        this.setState({ errors: {}, saving: true, pageModified: true });

        if (this.props.location.pathname === AppNavPaths.EmployeeNew) this.saveNewEmployee(employee);
        else this.saveEmployee(employee);
    }

    openChangeHistory = (historyType = null) => {
        this.setState({ showEmployeeHistory: true, employeeHistoryType: historyType });
    };

    onCloseEmployeeHistory = () => {
        this.setState({ showEmployeeHistory: false, employeeHistoryType: null });
    };

    onViewChangeHistory = () => {
        this.openChangeHistory();
    };

    onViewRestrictionHistory = () => {
        this.openChangeHistory('restrictions');
    };

    getEditPermissions = () => ({
        group: this.context.checkUserHasPermission(ApplicationPermissions.employee_group_edit),
        name: this.context.checkUserHasPermission(ApplicationPermissions.employee_name_edit),
        username: this.context.checkUserHasPermission(ApplicationPermissions.employee_username_edit),
        company: this.context.checkUserHasPermission(ApplicationPermissions.employee_payroll_company_edit),
        dispatchCompany: this.context.checkUserHasPermission(ApplicationPermissions.employee_dispatch_company_edit),
        number: this.context.checkUserHasPermission(ApplicationPermissions.employee_number_edit),
        notifications: this.context.checkUserHasPermission(ApplicationPermissions.employee_notifications_edit),
        import: this.context.checkUserHasPermission(ApplicationPermissions.employee_importupdates_edit),
        excludeDispatch: this.context.checkUserHasPermission(ApplicationPermissions.employee_exclude_dispatch),
        status: this.context.checkUserHasPermission(ApplicationPermissions.employee_accountstatus_edit),
        password: this.context.checkUserHasPermission(ApplicationPermissions.employee_password_edit),
        homeAddress: this.context.checkUserHasPermission(ApplicationPermissions.employee_home_address_edit),
        routingAddress: this.context.checkUserHasPermission(ApplicationPermissions.employee_routing_address_edit),
    });

    getViewPermissions = () => ({
        employeeHistory: this.context.checkUserHasPermission(ApplicationPermissions.employee_view_history),
        employeePay: this.context.checkUserHasPermission(ApplicationPermissions.employee_pay_view),
    });

    canBeImpersonated = () => {
        const { employee, groups } = this.state;
        let canBeImpersonated = false;
        if (employee?.dbGroups && groups) {
            canBeImpersonated = employee.dbGroups.some((group) => groups?.some(
                (systemGroup) => systemGroup.id === group.id
                    && (systemGroup.isImpersonatedGroup
                        || systemGroup.isFieldGroup),
            ));
        }

        return canBeImpersonated;
    };

    clearSaving = () => this.setState({ saving: false, errors: {} });

    handleSaveError = (err) => handleFormSaveError(this, err);

    passwordFields() {
        // Only required for new employees.
        // If password is empty for an existing employee, that's OK - password will be unchanged.
        const isPasswordRequired = !this.state.employee.id;

        const helpMessage = isPasswordRequired
            ? null
            : 'Leave blank if you do not want to reset the user\'s password.  If you change a password, the user will be required to update it on next login.';

        return (
            <>
                <Input
                    helpMessage={helpMessage}
                    label="Password"
                    id="password"
                    name="employee.password"
                    autoComplete="new-password"
                    type="password"
                    required={isPasswordRequired}
                    onChange={this.onChange}
                    value={this.state.employee.password ?? ''}
                    error="A Password is required"
                />
                <Input
                    formGroupHidden={!isPasswordRequired}
                    label="Confirm Password"
                    id="confirmPassword"
                    name="employee.confirmPassword"
                    autoComplete="new-password"
                    type="password"
                    required={isPasswordRequired}
                    onChange={this.onChange}
                    value={this.state.employee.confirmPassword ?? ''}
                    error="Password Confirmation is required and must match the password."
                />
            </>
        );
    }

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            let employee = null;
            const { id } = { ...this.props.match.params };
            const { search } = { ...this.props.location };
            const { user } = this.context;
            const { tenantSettings } = this.context.tenant;
            const userIsAdmin = user.isAdmin;

            // Are we onboarding?
            const onboardingRouted = search && new URLSearchParams(search).get('newHire');

            // Turn on onboarding iff routing + tenant has it enabled.
            const onboarding = tenantSettings.employeeOnboardingEnabled && onboardingRouted;

            // If onboarding a new emp, first check the user has the perm.
            if (
                onboarding
                && !id
                && (!tenantSettings.employeeOnboardingEnabled
                    || !this.context.checkUserHasPermission(ApplicationPermissions.employee_onboarding))
            ) {
                this.context.history.push(AppNavPaths.NoPermission);
                return false;
            }

            // If not onboarding a new emp, make sure the user has the regular emp add perm.
            if (
                !onboarding
                && !id
                && !this.context.checkUserHasPermission(ApplicationPermissions.employee_add)
            ) {
                this.context.history.push(AppNavPaths.NoPermission);
                return false;
            }

            this.setState({ loading: true });

            const { enableCertification } = tenantSettings;
            const canViewCertification = this.context.checkUserHasPermission(ApplicationPermissions.certification_view);

            // Permissions for new emp good, get the rest of the data.
            let [
                groups,
                employeeResponse,
                companies,
                statuses,
                employeeLongTermStatuses,
                employeeCertifications,
                countyGroups,
                contractScheduleTypes,
                tenantLanguages,
            ] = await Promise.all([
                httpClient.js(ApiRoutes.auth.groups()),
                id
                    ? httpClient.get(ApiRoutes.employee.byId(id), httpClient.format.none)
                    : new Employee(),
                httpClient.js(ApiRoutes.company.payrollCompanies()),
                httpClient.js(ApiRoutes.employeeAccountStatuses.all()),
                id
                    ? httpClient.js(ApiRoutes.employeeLongTermStatus.allForEmployee(id))
                    : [],
                (id && enableCertification && canViewCertification)
                    ? httpClient.js(ApiRoutes.employeeCertification.allForEmployee(id))
                    : [],
                httpClient.js(ApiRoutes.USCounties.groups()),
                httpClient
                    .js(ApiRoutes.typeAheads.contractScheduleTypes())
                    .catch(() => console.log('Unable to get contract work types.')),
                httpClient.js(ApiRoutes.language.tenant()).catch(() => []),
            ]);

            // Handle any issues when fetching the employee.
            if (id && !employeeResponse.ok) {
                // Handle erroneous links entered by the user.
                if (employeeResponse.status === 404) this.context.history.push(AppNavPaths.NotFound);
                else this.context.history.push(AppNavPaths.ServerError);
                return false;
            }
            employee = id
                ? await employeeResponse.json()
                : employeeResponse;

            // temporary variable (dbGroups) used as source of truth to define if employee can be impersonanted
            // user probably should not be able to impersonate selected employee until
            // new set of employee groups stored in database
            const dbGroups = employee.groups ?? [];
            employee.dbGroups = [...dbGroups];

            /**
             * For the onboarding process:
             *  - lock the emp's dispatch to the current user's dispatch company
             *  - lock the emp's parent company to the current user's parent company
             *  - default the user's group to the default group in tenant settings
             *  - set the initial account status
             * */
            if (onboarding && !id && !employee.id) {
                employee.companyId = user.companyId; // parent company
                employee.dispatchCompanyId = user.dispatchCompanyId; // dispatch company
                employee.accountStatus = statuses.find(
                    (x) => x.id === AccountStatus.NewHire,
                );

                const empGroup = groups.find(
                    (x) => x.id === tenantSettings.employeeOnboardingDefaultGroup,
                );
                employee.groups = [empGroup];
                employee.dbGroups = [empGroup];
            }

            if (employee.accountStatus?.id !== AccountStatus.NewHire) {
                // Don't display unconfirmed status if not onboarding.
                statuses = statuses.filter(
                    (x) => x.id !== AccountStatus.NewHire,
                );
            }

            let dispatchCompanies = [];

            // List dispatch companies for an existing, non-onboarded emp
            // Or for global admins (who dont have dispatch companies assigned) get the tenant dispatch companies.
            // Or list dispatch companies for the user onboarding the employee.
            // Only active dispatch companies are offered, plus any inactive company already assigned to this employee (ROX-3953).
            if (id && employee.companyId && userIsAdmin) {
                dispatchCompanies = await httpClient.js(
                    ApiRoutes.typeAheads.dispatchCompaniesForEmployee(id),
                );
            } else if (id && employee.companyId) {
                dispatchCompanies = await httpClient.js(
                    ApiRoutes.typeAheads.dispatchCompaniesForEmployee(id, employee.companyId),
                );
            } else if (userIsAdmin) {
                dispatchCompanies = await httpClient.js(
                    ApiRoutes.typeAheads.dispatchCompaniesForEmployee(),
                );
            } else if (employee.accountStatus?.id === AccountStatus.NewHire) {
                dispatchCompanies = await httpClient.js(
                    ApiRoutes.typeAheads.dispatchCompaniesForEmployee(null, user.companyId),
                );
            }

            // Map typeahead shape (value/label) back to the {id, companyName} the dispatch selects consume.
            dispatchCompanies = (dispatchCompanies ?? []).map((x) => ({
                id: x.value,
                companyName: x.label,
                parentCompanyId: x.parentCompanyId ?? null,
            }));

            let expandedDispatchCompanies = await httpClient.js(
                ApiRoutes.typeAheads.dispatchCompaniesForEmployee(id),
            );
            expandedDispatchCompanies = (expandedDispatchCompanies ?? []).map((x) => ({
                id: x.value,
                companyName: x.label,
                parentCompanyId: x.parentCompanyId ?? null,
            }));

            const longTermStatusManagementEnabled = tenantSettings.employeeLongTermStatusManagementEnabled;

            if (countyGroups?.length > 0) {
                // Ensure we only show locals
                countyGroups = countyGroups.filter((group) => group.usCountyGroupTypeId === 2);
            }

            this.setState({
                formValidated: false,
                addressFormValidated: false,
                payRateFormValidated: false,
                restrictionFormValidated: false,
                countyIncentiveFormValidated: false,
                saving: false,
                selectedAddress: null,
                selectedPayRate: null,
                selectedRestriction: null,
                selectedIncentive: null,
                companies,
                employee,
                groups,
                loading: false,
                originalData: employee,
                dispatchCompanies,
                statuses,
                user,
                isAdmin: userIsAdmin,
                onboarding,
                expandedDispatchCompanies,
                employeeLongTermStatuses,
                employeeCertifications,
                longTermStatusManagementEnabled,
                enableCertification,
                contractScheduleTypes,
                countyGroups, // All available county groups
                originalAccountStatusId: employee.accountStatus?.id,
                tenantLanguages: tenantLanguages.map((lang) => ({
                    value: lang.id,
                    label: LANGUAGE_LABELS[lang.name] || lang.description || lang.name,
                    code: lang.name,
                })),
            });
        }
    }

    toggleActiveAssignmentsModal = (showActiveJobNumbers) => {
        const { employee } = this.state;

        Modal.show({
            onSubmit: () => this.saveExistingEmployee(employee),
            title: 'Active Job Assignments',
            SaveButtonProps: {
                label: 'Continue',
            },
            body: () => (
                <TipAlert
                    tipText="Note"
                    label={(
                        <>
                            <strong>{employee.lastName},{employee.firstName} </strong>
                            is currently assigned to the following jobs:
                            {' '}
                            <br />
                            {showActiveJobNumbers}
                            .
                            <br />
                            If you mark this employee Inactive, they will be removed from these assignments.
                            Do you want to continue?
                        </>
                    )}
                />
            ),
        });
    };

    saveEmployee = async (employee) => {
        try {
            if (employee.accountStatus.id == 2) { // inactive
                // check employee has active assignments (like Scheduled, Noresponse and confirmed but not submitted timesheet)
                const jobNumbers = await httpClient.js(
                    ApiRoutes.jobAssignment.getEmployeeActiveAssignments(employee.id));

                if (jobNumbers.length > 0) {
                    const activeJobNumbers = jobNumbers.join(', ');
                    this.setState({ saving: false });

                    this.toggleActiveAssignmentsModal(activeJobNumbers);
                    return false;
                } else {
                    this.saveExistingEmployee(employee);
                }
            } else {
                this.saveExistingEmployee(employee);
            }
        } catch (error) {
            notifyError(error.toString());
        } finally {
            this.clearSaving();
        }
    };

    onSelectChanged = onReactSelectChanged;
    saveExistingEmployee = async (employee) => {
        this.setState({ showActiveAssignmentsModal: false, saving: true, showActiveJobNumbers: [] });
        const response = await httpClient.put(
            ApiRoutes.employee.update(employee.id),
            employee,
            httpClient.format.none,
        );
        if (response.redirected) {
            window.location.href = response.url;
        } else if (response.ok) {
            const serviceResponse = await response.json();
            if (serviceResponse.result === 'SUCCESS') {
                notifySuccess();
                this.setState({ pageModified: false });
                this.populateState();
            } else {
                notifyError(serviceResponse.message);
            }
        }
    };
    saveNewEmployee = async (employee) => {
        try {
            const response = await httpClient.post(
                ApiRoutes.employee.create(),
                employee,
                httpClient.format.none,
            );
            if (response.redirected) {
                window.location.href = response.url;
            } else if (response.ok) {
                const serviceResponse = await response.json();
                if (serviceResponse.result === 'SUCCESS') {
                    notifySuccess();
                    const employeeId = serviceResponse.data;
                    if (employee.accountStatus.id === AccountStatus.NewHire) {
                        this.context.history.push(`${AppNavPaths.Employees}`);
                    } else {
                        this.context.history.push(
                            `${AppNavPaths.Employee}/${employeeId}`,
                        );
                    }
                } else {
                    notifyError(serviceResponse.message);
                }
            }
        } catch (error) {
            notifyError(error.toString());
        } finally {
            this.clearSaving();
        }
    };

    // True when at least one of the employee's selected groups is a field personnel
    // group. The on-call settings only apply to field personnel. Selected groups loaded
    // from the server may not carry the isFieldGroup flag, so cross-reference against the
    // full system groups list by id (mirrors canBeImpersonated).
    isFieldPersonnel = () => {
        const { employee, groups } = this.state;
        return (employee?.groups ?? []).some((g) => g.isFieldGroup
            || (groups ?? []).some((sg) => sg.id === g.id && sg.isFieldGroup));
    };

    // Show multi dispatch selection if at least one group has been toggled as Expand Dispatching.
    showExpandDispatching = () => {
        const { employee } = { ...this.state };
        return (
            ((employee?.groups ?? []).filter((x) => x.expandDispatching) ?? [])
                .length > 0
        );
    };

    toggleEmployeeLongTermStatusForm = (show) => this.setState(
        (state) => ((state.showEmployeeLongTermStatusForm = show), state),
    );

    toggleEmployeeCertificationForm = (show) => this.setState({ showEmployeeCertificationForm: show });

    async updateEmployee() {
        const { employee } = this.state;
        const emp = await httpClient.js(ApiRoutes.employee.byId(employee.id));
        this.setState({ employee: emp });
    }

    onHomeLocalsChanged = (value) => {
        const { employee } = { ...this.state };

        employee.usCountyGroups = value.map((group) => {
            const newValue = { ...group };
            delete newValue.contractUnionWorkTypes;

            return newValue;
        });

        this.setState({ employee });
    };

    getMobilePhoneError = () => {
        const { employee } = this.state;
        const { employeeOnboardingEnabled } = {
            ...this.context.tenant.tenantSettings,
        };

        const isOnboarding = employeeOnboardingEnabled && employee.accountStatus?.id === AccountStatus.NewHire;

        if (!employee.mobilePhone) {
            if (!isOnboarding && employee.smsNotify) {
                return 'A valid phone number is required when SMS notifications are enabled.';
            }

            if (isOnboarding) {
                return 'Ensure the phone number is valid. (555-555-5555)';
            }

            return '';
        }

        if (!new RegExp(patterns.htmlPhone).test(employee.mobilePhone)) {
            return 'Invalid phone number format.';
        }

        return '';
    };

    getActions = () => {
        const { impersonateLoading, employee } = this.state;
        const employeeId = employee?.id;

        const actions = [];

        if (employeeId) {
            actions.push({
                key: 'view',
                onClick: this.onViewChangeHistory,
                icon: faTasks,
                label: 'View Change History',
                CanProps: {
                    I: 'history',
                    a: 'employee',
                },
            });

            if (this.canBeImpersonated()) {
                actions.push({
                    key: 'impersonate',
                    onClick: this.onClickImpersonation,
                    icon: faUserFriends,
                    label: 'Impersonate',
                    disabled: impersonateLoading,
                    CanProps: {
                        I: 'toggle',
                        a: 'impersonation',
                    },
                });
            }
        }

        if (actions.length > 0) {
            return actions;
        }

        return null;
    };

    renderHeaderRight = () => {
        const { employee } = this.state;
        const newEmployee = (employee.id ?? 0) <= 0;

        if (newEmployee) {
            return null;
        }

        return (
            <div className="d-flex align-items-center justify-content-end mr-2">
                <Chip
                    id="employee-eligible-date"
                    color="info"
                    outlined
                    text={employee.eligibleDate}
                />

                <UncontrolledTooltip target="employee-eligible-date">
                    Employee Creation Date
                </UncontrolledTooltip>
            </div>
        );
    };

    render() {
        const {
            expandedDispatchCompanies,
            companies,
            loading,
            originalData,
            employee,
            groups,
            dispatchCompanies,
            statuses,
            isAdmin,
            longTermStatusManagementEnabled,
            enableCertification,
            employeeLongTermStatuses,
            employeeCertifications,
            selectedEmployeeLongTermStatus,
            showEmployeeLongTermStatusForm,
            showPayRateForm,
            selectedPayRate,
            pageModified,
            countyGroups,
            contractScheduleTypes,
            unlockLoading,
            tenantLanguages,
            showCountyIncentiveForm,
            selectedIncentive,
            showEmployeeHistory,
            employeeHistoryType,
        } = { ...this.state };

        const employeeArray = [employee];

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const isUnionWorkEnforced = !!tenantSettings.enforceContractUnionWork;

        if (employee) {
            employeeArray[0].displayName = `${employee.lastName}, ${employee.firstName}`;
        }

        if (
            loading
            || !this.context.tenant.tenantSettings
            || !groups.length
        ) {
            return (
                <FlexColumnCenter className="align-items-center p-4 w-100">
                    <FormCircularProgress />
                </FlexColumnCenter>
            );
        }
        const adminSelected = (employee.groups || [])
            .map((v) => v.groupName)
            .includes('Administrator');
        const { employeeOnboardingEnabled } = {
            ...this.context.tenant.tenantSettings,
        };
        const { employeeImportSwitchEnabled } = {
            ...this.context.tenant.tenantSettings,
        };
        const new_emp = (employee.id ?? 0) <= 0;
        const pwdFields = this.passwordFields();
        const isOnboarding = employeeOnboardingEnabled && employee.accountStatus?.id === AccountStatus.NewHire;

        let saveEmployeeButtonText = 'Save New Employee';

        if (!new_emp) {
            saveEmployeeButtonText = 'Save';
        } else if (isOnboarding) {
            saveEmployeeButtonText = 'Save New Hire';
        }

        // field editing permissions
        const canEdit = this.getEditPermissions();

        const canView = this.getViewPermissions();

        // Don't show unlock if employee is currently inactive
        const canUnlockEmployee = this.context.checkUserHasPermission(ApplicationPermissions.employee_unlock)
            && employee.accountStatus?.id != AccountStatus.Inactive;

        // Only show the locked message if:
        const wasOriginallyInactive = this.state.originalAccountStatusId == AccountStatus.Inactive;

        const shouldShowLockedMessage = employee.locked
            && employee.accountStatus?.id != AccountStatus.Inactive
            && !wasOriginallyInactive;

        // For expanded dispatch options, no need to include employee's default dispatch in the list.  Filter it out.
        const expandedDispatchOptions = (expandedDispatchCompanies ?? []).filter(
            (x) => x.id !== employee.dispatchCompanyId,
        ) ?? [];
        const selectedExpandedDispatchCompanies = expandedDispatchOptions.filter((x) => (employee?.expandedDispatchCompanies ?? []).includes(x.id));
        const selectedDispatchCompany = (dispatchCompanies ?? []).find(
            (x) => x.id
                === employee.dispatchCompanyId,
        );

        const isDispatchDisabled =
            !isAdmin &&
            (isOnboarding ||
                !employee.companyId ||
                !(dispatchCompanies?.length) ||
                !canEdit.dispatchCompany
            );
        const fallbackDispatchCompany = expandedDispatchCompanies?.[0] ?? null;
        const dispatchCompanyValue = isDispatchDisabled
            ? (selectedDispatchCompany || fallbackDispatchCompany)
            : (selectedDispatchCompany || null);

        return (
            <>
                <FormPrompt
                    when={
                        !this.state.saving
                        && (pageModified || !isEqual(originalData, employee))
                    }
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="empForm"
                    formHeadingIcon={faUser}
                    formHeading={new_emp ? 'New Employee' : 'Edit Employee'}
                    formName="empForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    headerRight={this.renderHeaderRight()}
                    actions={this.getActions()}
                    isValidated={this.state.formValidated}
                    saving={this.state.saving}
                    errors={this.state.errors}
                    loading={this.state.loading || this.state.impersonateLoading}
                    backUrl={AppNavPaths.Employees}
                    backLabel="Back to Employees"
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            {!isOnboarding && (
                                <ValidatedSelect
                                    id="groups"
                                    name="groups"
                                    label="Groups"
                                    isMulti
                                    required
                                    isDisabled={!canEdit.group}
                                    options={groups}
                                    value={employee.groups ?? ''}
                                    getOptionLabel={(option) => option.groupName}
                                    getOptionValue={(option) => option.id}
                                    onChange={
                                        canEdit.group
                                            ? this.onGroupChange
                                            : undefined
                                    }
                                    error="At least one group selection is required."
                                />
                            )}

                            <Row>
                                <Col
                                    xs={12}
                                    md={6}
                                >
                                    <Input
                                        label="First Name"
                                        id="firstName"
                                        name="employee.firstName"
                                        autoComplete="off"
                                        required
                                        disabled={!new_emp && !canEdit.name}
                                        onChange={(event) => {
                                            if (isOnboarding) {
                                                event.persist();
                                                this.onFirstNameChanged(
                                                    event,
                                                );
                                            } else if (canEdit.name) {
                                                this.onChange(event);
                                            }
                                        }}
                                        value={employee.firstName ?? ''}
                                        error="First name is required."
                                    />
                                </Col>

                                <Col
                                    xs={12}
                                    md={6}
                                >
                                    <Input
                                        label="Last Name"
                                        id="lastName"
                                        name="employee.lastName"
                                        autoComplete="off"
                                        required
                                        disabled={!new_emp && !canEdit.name}
                                        onChange={(event) => {
                                            if (isOnboarding) {
                                                event.persist();
                                                this.onLastNameChanged(
                                                    event,
                                                );
                                            } else if (canEdit.name) {
                                                this.onChange(event);
                                            }
                                        }}
                                        value={employee.lastName ?? ''}
                                        error="Last name is required."
                                    />
                                </Col>
                            </Row>

                            {Boolean(tenantSettings.quirksEnabled) && (
                                <>
                                    {isOnboarding
                                        && (!employee.lastName || !employee.firstName) && (
                                            <FlexStartRow className="pl-3 pb-3 text-warning">
                                                <i className="mr-2 fa fa-exclamation-triangle" />
                                                <small>
                                                    Complete the user&apos;s first
                                                    name and last name to
                                                    generate the username.
                                                </small>
                                            </FlexStartRow>
                                        )}

                                    {isOnboarding && employee.lastName && employee.firstName && employee.userName && (
                                        <span className="ml-3 pb-3 text-muted font-weight-bold">
                                            {' '}
                                            {employee.userName}
                                        </span>
                                    )}

                                    {!isOnboarding && (
                                        <Input
                                            label="Username"
                                            id="userName"
                                            disabled={!canEdit.username}
                                            name="employee.userName"
                                            autoComplete="off"
                                            required
                                            onChange={
                                                canEdit.username
                                                    ? this.onChange
                                                    : undefined
                                            }
                                            value={employee.userName ?? ''}
                                            error="Username is required."
                                        />
                                    )}
                                </>
                            )}

                            {!isOnboarding && (
                                <ValidatedSelect
                                    id="companySelect"
                                    name="companySelect"
                                    label="Company"
                                    required
                                    isDisabled={!canEdit.company}
                                    options={companies}
                                    value={
                                        (companies ?? []).find(
                                            (x) => x.id
                                                === employee.companyId,
                                        ) ?? ''
                                    }
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.companyName}
                                    error="A Company selection is required"
                                    onChange={
                                        canEdit.company
                                            ? this.onCompanyChanged
                                            : undefined
                                    }
                                />
                            )}

                            <ValidatedSelect
                                id="dispatchCompany"
                                label="Home Dispatching"
                                required={
                                    (dispatchCompanies ?? []).length
                                }
                                error="A Home Dispatching company selection is required"
                                name="dispatchCompany"
                                isDisabled={isDispatchDisabled}
                                options={dispatchCompanies}
                                value={dispatchCompanyValue}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.companyName}
                                onChange={
                                    canEdit.dispatchCompany
                                        ? this.onDispatchCompanyChanged
                                        : undefined
                                }
                            />

                            {
                                // If the employee belongs to at least one group with an expanded dispatching flag set,
                                // allow assignment of additional dispatching (if they have the permissions).
                                this.showExpandDispatching() && (
                                    <ValidatedSelect
                                        id="expandedDispatchCompanies"
                                        name="expandedDispatchCompanies"
                                        label="Expanded Dispatching"
                                        options={
                                            expandedDispatchOptions
                                        }
                                        isMulti
                                        value={
                                            selectedExpandedDispatchCompanies
                                        }
                                        getOptionValue={(option) => option.id}
                                        getOptionLabel={(option) => option.companyName}
                                        onChange={
                                            canEdit.dispatchCompany
                                                ? this
                                                    .onExpandedDispatchCompaniesChanged
                                                : undefined
                                        }
                                    />
                                )
                            }

                            {((canEdit.number && new_emp) || !new_emp) && (
                                <Row>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <Input
                                            label="ADP Employee Number"
                                            id="employeeNumber"
                                            name="employee.externalId"
                                            autoComplete="off"
                                            disabled={!canEdit.number}
                                            onChange={
                                                !canEdit.number
                                                    ? undefined
                                                    : this.onChange
                                            }
                                            value={employee.externalId ?? ''}
                                        />
                                    </Col>

                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <Input
                                            label="Payroll File Number"
                                            id="payrollFileNumber"
                                            name="employee.payrollFileNumber"
                                            autoComplete="off"
                                            disabled={!canEdit.number}
                                            onChange={
                                                !canEdit.number
                                                    ? undefined
                                                    : this.onChange
                                            }
                                            value={employee.payrollFileNumber ?? ''}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <FormGroup>
                                <Input
                                    formGroupClassName="mb-0"
                                    label="Phone"
                                    id="mobilePhone"
                                    placeholder="ex: 555-555-5555"
                                    name="employee.mobilePhone"
                                    pattern={
                                        patterns.htmlPhone
                                    }
                                    autoComplete="off"
                                    type="tel"
                                    disabled={!canEdit.notifications}
                                    onChange={
                                        canEdit.notifications
                                            ? this.onMobilePhoneChanged
                                            : undefined
                                    }
                                    value={employee.mobilePhone ?? ''}
                                    required={
                                        !isOnboarding && employee.smsNotify
                                    }
                                    error={this.getMobilePhoneError()}
                                />
                                {employee.mobilePhone && (
                                    <FormCheckbox
                                        className="mt-2 pl-2"
                                        id="chkSmsNotify"
                                        checked={
                                            employee.smsNotify ?? false
                                        }
                                        disabled={!canEdit.notifications}
                                        readOnly={!canEdit.notifications}
                                        onChange={
                                            canEdit.notifications
                                                ? this.onSMSNotifyChanged
                                                : undefined
                                        }
                                        labelText="Enable SMS Notifications"
                                    />
                                )}
                            </FormGroup>
                            <FormGroup>
                                <Input
                                    formGroupClassName="mb-0"
                                    label={tenantSettings.quirksEnabled ? 'Email' : 'Email/Username'}
                                    id="email"
                                    placeholder="ex: employee@provider.com"
                                    name="employee.email"
                                    autoComplete="off"
                                    type="email"
                                    required
                                    disabled={!canEdit.notifications}
                                    onChange={
                                        canEdit.notifications
                                            ? this.onEmailChanged
                                            : undefined
                                    }
                                    value={employee.email ?? ''}
                                    error="Employee email is required."
                                />
                                {employee.email && (
                                    <FormCheckbox
                                        className="mt-2 pl-2"
                                        id="chkEmailNotify"
                                        disabled={!canEdit.notifications}
                                        readOnly={!canEdit.notifications}
                                        checked={
                                            employee.emailNotify ?? false
                                        }
                                        onChange={
                                            canEdit.notifications
                                                ? this.onEmailNotifyChanged
                                                : undefined
                                        }
                                        labelText="Enable Email Notifications"
                                    />
                                )}
                            </FormGroup>

                            {tenantLanguages.length > 1 && (
                                <ValidatedSelect
                                    label="Preferred Language"
                                    id="preferredLanguageId"
                                    name="employee.preferredLanguageId"
                                    options={tenantLanguages}
                                    value={tenantLanguages.find((lang) => lang.value === employee.preferredLanguageId)}
                                    isClearable
                                    onChange={this.onSelectChanged}
                                />
                            )}

                            {!isOnboarding && (
                                <FormGroup>
                                    <FormLabel label="Configuration" />
                                    {employeeImportSwitchEnabled && (
                                        <FormCheckbox
                                            className="mt-2 mb-2"
                                            id="chkIncludeImport"
                                            checked={
                                                employee.systemAdded
                                                ?? false
                                            }
                                            disabled={!canEdit.import}
                                            readOnly={!canEdit.import}
                                            onChange={
                                                canEdit.import
                                                    ? this
                                                        .onIncludeImportChanged
                                                    : undefined
                                            }
                                            labelText="Include with Import Updates"
                                        />
                                    )}
                                    <FormCheckbox
                                        className="mt-2 mb-2"
                                        id="chkExcludeDispatch"
                                        checked={
                                            employee.excludeDispatch
                                            ?? false
                                        }
                                        disabled={!canEdit.excludeDispatch}
                                        readOnly={!canEdit.excludeDispatch}
                                        onChange={
                                            canEdit.excludeDispatch
                                                ? this
                                                    .onExcludeDispatchChanged
                                                : undefined
                                        }
                                        labelText="Exclude from Dispatch Board"
                                    />
                                    {!employee.excludeDispatch && this.isFieldPersonnel() && (
                                        <>
                                            <FormCheckbox
                                                className="mt-2 mb-2"
                                                id="chkAvailableForOnCall"
                                                checked={
                                                    employee.isFlaggerAvailableForOnCall
                                                    ?? false
                                                }
                                                disabled={!canEdit.excludeDispatch || employee.isOnCallRestricted}
                                                readOnly={!canEdit.excludeDispatch}
                                                onChange={
                                                    canEdit.excludeDispatch
                                                        ? this
                                                            .onAvailableForOnCallChanged
                                                        : undefined
                                                }
                                                labelText="Available for last minute call in and emergency jobs"
                                            />
                                            <FormCheckbox
                                                className="mt-2 mb-2"
                                                id="chkDisableOnCallBanner"
                                                checked={
                                                    employee.isOnCallRestricted
                                                    ?? false
                                                }
                                                disabled={!canEdit.excludeDispatch}
                                                readOnly={!canEdit.excludeDispatch}
                                                onChange={
                                                    canEdit.excludeDispatch
                                                        ? this
                                                            .onDisableOnCallBannerChanged
                                                        : undefined
                                                }
                                                labelText="Disable access for last minute call in and emergency jobs notification banner"
                                            />
                                        </>
                                    )}
                                </FormGroup>
                            )}

                            <ValidatedSelect
                                isDisabled={
                                    isOnboarding || !canEdit.status
                                }
                                id="accountStatus"
                                name="accountStatus"
                                label="Account Status"
                                options={statuses}
                                value={
                                    (statuses ?? []).find(
                                        (x) => x.id
                                            === employee?.accountStatus?.id,
                                    ) ?? ''
                                }
                                getOptionLabel={(option) => option.description}
                                getOptionValue={(option) => option.id}
                                onChange={
                                    canEdit.status
                                        ? this.onAccountStatusChanged
                                        : undefined
                                }
                                required={!isOnboarding}
                                error="Account status (Active/Inactive) is required."
                                helperText={isOnboarding && 'When the employee receives an employee number, the status will migrate to Active.'}
                            />

                            {shouldShowLockedMessage && (
                                <FormGroup>
                                    <div className="d-flex align-items-center p-2 bg-danger text-white rounded">
                                        <FontAwesomeIcon
                                            icon={faLock}
                                            className="mr-2"
                                        />
                                        <span className="mr-3">Account Locked</span>
                                        {canUnlockEmployee && (
                                            <Button
                                                size="sm"
                                                color="light"
                                                disabled={unlockLoading}
                                                onClick={this.onUnlockEmployee}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faUnlock}
                                                    className="mr-1"
                                                />
                                                {unlockLoading ? 'Unlocking...' : 'Unlock Account'}
                                            </Button>
                                        )}
                                    </div>
                                </FormGroup>
                            )}
                            {pwdFields}
                        </FormGroupColumn>
                    </GroupedRow>

                    {!new_emp && !isOnboarding && (
                        <>
                            <EmployeeMetrics employeeId={employee.id} />

                            {Boolean(isUnionWorkEnforced) && (
                                <Can
                                    I="view"
                                    a="employee_pay"
                                >
                                    <SubHeading>Union Pay Schedule</SubHeading>
                                    <GroupedRow className="justify-content-center">
                                        <Col xs={6}>
                                            <ValidatedSelect
                                                id="usCountyGroups"
                                                name="usCountyGroups"
                                                label="Home Local(s)"
                                                isMulti
                                                options={countyGroups}
                                                value={employee.usCountyGroups ?? ''}
                                                onChange={this.onHomeLocalsChanged}
                                                getOptionLabel={(option) => option.description ?? option.name ?? option.label}
                                                getOptionValue={(option) => option.id ?? option.value}
                                                error="At least one home local must be selected."
                                            />

                                            <FormGroup>
                                                <FormCheckbox
                                                    id="employee.hasPersonalPayrollSchedule"
                                                    name="employee.hasPersonalPayrollSchedule"
                                                    checked={employee.hasPersonalPayrollSchedule ?? false}
                                                    onChange={this.onChange}
                                                    labelText="This employee has their own pay schedule"
                                                />
                                            </FormGroup>

                                            <Collapse isOpen={employee.hasPersonalPayrollSchedule}>
                                                <Block>
                                                    <ValidatedSelect
                                                        id="contractScheduleTypeId"
                                                        name="employee.contractScheduleTypeId"
                                                        label="Schedule"
                                                        options={contractScheduleTypes}
                                                        required
                                                        error="A pay schedule selection is required"
                                                        value={(
                                                            contractScheduleTypes ?? []
                                                        ).find(
                                                            (x) => employee.contractScheduleTypeId === x.value,
                                                        ) ?? ''}
                                                        onChange={
                                                            this.onSelectChanged
                                                        }
                                                    />
                                                </Block>
                                            </Collapse>
                                        </Col>
                                    </GroupedRow>
                                </Can>
                            )}

                            <Can
                                I="view"
                                a="employee_pay"
                            >
                                <SubHeading>Pay Rate</SubHeading>
                                <GroupedRow>
                                    <Col sm="12">
                                        <EmployeePayRateInfo
                                            onAddPayRate={this.onAddPayRate}
                                            onEditPayRate={
                                                this.onEditPayRate
                                            }
                                            payRates={
                                                this.state.employee.payRates
                                            }
                                        />
                                    </Col>
                                </GroupedRow>
                            </Can>

                            {isUnionWorkEnforced && (
                                <Can
                                    I="view"
                                    a="employee_pay"
                                >
                                    <SubHeading>County Incentives</SubHeading>
                                    <GroupedRow>
                                        <Col sm="12">
                                            <EmployeeCountyIncentiveInfo
                                                onAddIncentive={this.onAddIncentive}
                                                onEditIncentive={this.onEditIncentive}
                                                incentives={
                                                    this.state.employee.employeeUSCountyIncentives
                                                }
                                            />
                                        </Col>
                                    </GroupedRow>
                                </Can>
                            )}

                            <SubHeading>Addresses</SubHeading>
                            <GroupedRow>
                                <Col sm="12">
                                    <EmployeeAddressInfo
                                        onAddAddress={this.onAddAddress}
                                        addresses={
                                            this.state.employee.addresses
                                        }
                                        showRoutingAddress={!adminSelected}
                                        onEditAddress={this.onEditAddress}
                                    />
                                </Col>
                            </GroupedRow>

                            <Can
                                I="view"
                                a="employee_restriction"
                            >
                                <SubHeading>Restrictions</SubHeading>
                                <GroupedRow>
                                    <Col sm="12">
                                        <EmployeeRestrictionInfo
                                            onAddRestriction={
                                                this.onAddRestriction
                                            }
                                            restrictions={
                                                this.state.employee
                                                    .restrictions
                                            }
                                            onEditRestriction={
                                                this.onEditRestriction
                                            }
                                            onViewHistory={
                                                this.state.employee.id
                                                    ? this.onViewRestrictionHistory
                                                    : null
                                            }
                                            employeeId={this.state.employee.id}
                                        />
                                    </Col>
                                </GroupedRow>
                            </Can>

                            {longTermStatusManagementEnabled && (
                                <Can
                                    I="view"
                                    a="employeelongtermstatus"
                                >
                                    <SubHeading>
                                        Long-Term Statuses
                                    </SubHeading>
                                    <GroupedRow>
                                        <Col sm="12">
                                            <EmployeeLongTermStatusInfo
                                                onAddEmployeeLongTermStatus={
                                                    this
                                                        .onAddEmployeeLongTermStatus
                                                }
                                                employeeLongTermStatuses={
                                                    employeeLongTermStatuses
                                                }
                                                onEditEmployeeLongTermStatus={
                                                    this
                                                        .onEditEmployeeLongTermStatus
                                                }
                                            />
                                        </Col>
                                    </GroupedRow>
                                </Can>
                            )}

                            {enableCertification && this.context.checkUserHasPermission(ApplicationPermissions.certification_view) && (
                                <>
                                    <SubHeading>Certifications</SubHeading>
                                    <GroupedRow>
                                        <Col sm="12">
                                            <EmployeeCertificationInfo
                                                onAddEmployeeCertification={this.onAddEmployeeCertification}
                                                employeeCertifications={employeeCertifications}
                                                onEditEmployeeCertification={this.onEditEmployeeCertification}
                                            />
                                        </Col>
                                    </GroupedRow>
                                </>
                            )}

                            {Boolean(employee.id) && this.context.checkUserHasPermission(ApplicationPermissions.timesheet_view) && (
                                <>
                                    <SubHeading>Recent Timesheets</SubHeading>
                                    <GroupedRow>
                                        <Col sm="12">
                                            <EmployeeRecentTimesheetsInfo
                                                employeeId={employee.id}
                                            />
                                        </Col>
                                    </GroupedRow>
                                </>
                            )}
                        </>
                    )}
                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="empForm"
                        >
                            <FontAwesomeIcon
                                className="mr-2"
                                icon={faSave}
                            />
                            {saveEmployeeButtonText}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>

                {Boolean(employee.id && canView.employeeHistory) && (
                    <EmployeeHistoryForm
                        show={showEmployeeHistory}
                        employeeId={employee.id}
                        historyType={employeeHistoryType}
                        onClose={this.onCloseEmployeeHistory}
                    />
                )}

                {!isOnboarding && (
                    <>
                        {(canEdit.homeAddress || canEdit.routingAddress) && (
                            <AddressFormNew
                                id="empAddressForm"
                                ref={this.addressFormRef}
                                namePrefix=""
                                contractUsesPrevailingWage={false}
                                countryId={employee.countryId}
                                countryCode={employee.countryCode}
                                onSaveCallback={(address) => this.onSaveAddress(address)}
                                timeZone={selectedDispatchCompany?.location?.timeZone}
                            />
                        )}

                        {canView.employeePay && (
                            <>
                                <EmployeePayRateForm
                                    payRate={selectedPayRate}
                                    ref={this.employeePayRateFormRef}
                                    show={showPayRateForm}
                                    onClose={() => this.setState({
                                        showPayRateForm: false,
                                    })}
                                    onSaveCallback={this.onSavePayRate}
                                    onChange={this.onPayRateChange}
                                    setIsValidated={(value) => {
                                        this.setState({
                                            payRateFormValidated: value,
                                        });
                                    }}
                                />
                                {isUnionWorkEnforced && (
                                    <EmployeeCountyIncentiveForm
                                        countyIncentive={selectedIncentive}
                                        ref={this.employeeCountyIncentiveFormRef}
                                        show={showCountyIncentiveForm}
                                        onClose={() => this.setState({
                                            showCountyIncentiveForm: false,
                                        })}
                                        onSaveCallback={this.onSaveCountyIncentive}
                                        onChange={this.onIncentiveChange}
                                        onCountyChange={this.onCountyChange}
                                        onRemoveIncentive={this.onRemoveIncentive}
                                        isValidated={this.state.countyIncentiveFormValidated}
                                        setIsValidated={(value) => {
                                            this.setState({
                                                countyIncentiveFormValidated: value,
                                            });
                                        }}
                                    />
                                )}
                            </>
                        )}

                        <Can
                            do="edit"
                            on="employee_restriction"
                        >
                            <EmployeeRestrictionForm
                                ref={this.restrictionFormRef}
                                employeeId={this.state.employee.id}
                                existingRestrictions={this.state.employee.restrictions}
                                show={this.state.showRestrictionForm}
                                onClose={() => this.setState({
                                    showRestrictionForm: false,
                                })}
                                onRestrictionChanged={this.refreshEmployeeRestrictions}
                            />
                        </Can>

                        <Can
                            do="edit"
                            on="employeelongtermstatus"
                        >
                            <EmployeeLongTermStatusForm
                                onClose={() => this.toggleEmployeeLongTermStatusForm(false)}
                                ref={this.employeeLongTermStatusFormRef}
                                show={showEmployeeLongTermStatusForm}
                                toggleShow={
                                    this.toggleEmployeeLongTermStatusForm
                                }
                                employeeLongTermStatus={
                                    selectedEmployeeLongTermStatus
                                }
                                onSaveCallback={this.onSaveLongTermStatus}
                            />
                        </Can>

                        <EmployeeCertificationForm
                            onClose={() => this.toggleEmployeeCertificationForm(false)}
                            ref={this.employeeCertificationFormRef}
                            show={this.state.showEmployeeCertificationForm}
                            toggleShow={this.toggleEmployeeCertificationForm}
                            employeeCertification={this.state.selectedEmployeeCertification}
                            employeeCertifications={employeeCertifications}
                            onSaveCallback={this.onSaveCertification}
                        />
                    </>
                )}
            </>
        );
    }
    // #endregion
}

export default EmployeeForm;
